import React from 'react';

import loadable from 'loadable-components';
import PageLoading from 'app/components/common/PageLoading';
import SignInRequiredRoute from 'app/components/routes/SignInRequiredRoute';

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
  });

const delayLoad = (func, ms = 200) => {
  if (typeof window === 'undefined') return func;
  return () => Promise.all([func, delay(ms)]).then((val) => val[0]());
};

const getAsyncComponent = (asyncModule, webpackModule) =>
  loadable(delayLoad(asyncModule), {
    modules: [webpackModule],
    LoadingComponent: () => <PageLoading />,
  });

const routes = [
  {
    name: 'Home',
    screenName: '홈',
    path: '/',
    component: getAsyncComponent(() => import('./front/Home'), './front/Home'),
  },
  {
    name: 'SignIn',
    path: '/signin',
    component: getAsyncComponent(
      () => import('./front/SignIn'),
      './front/SignIn'
    ),
  },
  {
    name: 'SignInWeb',
    path: '/signin-web',
    component: getAsyncComponent(
      () => import('./front/SignIn/index.web.jsx'),
      './front/SignIn/index.web.jsx'
    ),
  },
  {
    name: 'SignInOauth',
    path: '/signin/:type/oauth',
    component: getAsyncComponent(
      () => import('./front/SignInOauth'),
      './front/SignInOauth'
    ),
  },
  {
    name: 'CurationList',
    screenName: '큐레이션',
    path: '/curations/:uuid',
    component: getAsyncComponent(
      () => import('./front/CurationList'),
      './front/CurationList'
    ),
  },
  {
    name: 'SectionList',
    screenName: '섹션',
    path: '/sections/:uuid',
    component: getAsyncComponent(
      () => import('./front/CurationList'),
      './front/CurationList'
    ),
  },
  {
    name: 'CurationList',
    screenName: '큐레이션',
    path: '/curations/:uuid',
    component: getAsyncComponent(
      () => import('./front/CurationList'),
      './front/CurationList'
    ),
  },
  {
    name: 'Roulette',
    screenName: '룰렛',
    path: '/roulette',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Roulette'),
      './front/Roulette'
    ),
  },
  {
    name: 'NoticeList',
    screenName: '공지사항',
    path: '/notices',
    component: getAsyncComponent(
      () => import('./front/NoticeList'),
      './front/NoticeList'
    ),
  },
  {
    name: 'NoticeDetail',
    screenName: '공지사항 상세',
    path: '/notices/:noticeId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/NoticeDetail'),
      './front/NoticeDetail'
    ),
  },
  {
    name: 'Library',
    screenName: '메인/보관함',
    path: '/library',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Library'),
      './front/Library'
    ),
  },
  {
    name: 'Setting',
    path: '/settings',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Setting'),
      './front/Setting'
    ),
  },
  {
    name: 'AccountSetting',
    path: '/account-settings',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AccountSetting'),
      './front/AccountSetting'
    ),
  },
  {
    name: 'AccountSettingEdit',
    path: '/account-settings/:field',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/AccountSettingEdit'),
      './front/AccountSettingEdit'
    ),
  },
  {
    name: 'StoryDetail',
    screenName: '작품 상세',
    path: '/stories/:storyId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/StoryDetail/route'),
      './front/StoryDetail/route'
    ),
  },
  {
    name: 'EpisodeDetail',
    screenName: '회차 상세',
    path: '/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/EpisodeDetail'),
      './front/EpisodeDetail'
    ),
  },
  {
    name: 'StoryCommentList',
    screenName: '작품 댓글',
    path: '/stories/:storyId(\\d+)/comments',
    component: getAsyncComponent(
      () => import('./front/StoryCommentList'),
      './front/StoryCommentList'
    ),
  },
  {
    name: 'EpisodeCommentList',
    screenName: '회차 댓글',
    path: '/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/comments',
    component: getAsyncComponent(
      () => import('./front/EpisodeCommentList'),
      './front/EpisodeCommentList'
    ),
  },
  {
    name: 'ReplyCommentList',
    screenName: '답글 목록',
    path: '/stories/:storyId(\\d+)/comments/:commentId(\\d+)/replies',
    component: getAsyncComponent(
      () => import('./front/ReplyCommentList'),
      './front/ReplyCommentList'
    ),
  },
  {
    name: 'UserDetail',
    screenName: '유저 상세',
    path: '/users/:userId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/UserDetail'),
      './front/UserDetail'
    ),
  },
  {
    name: 'FollowerList',
    screenName: '유저 상세/팔로워',
    path: '/users/:userId(\\d+)/followers',
    component: getAsyncComponent(
      () => import('./front/UserFollowerList'),
      './front/FollowerList'
    ),
  },
  {
    name: 'FollowingList',
    screenName: '유저 상세/팔로잉',
    path: '/users/:userId(\\d+)/followings',
    component: getAsyncComponent(
      () => import('./front/UserFollowingList'),
      './front/FollowingList'
    ),
  },
  {
    name: 'NotificationList',
    screenName: '알림함',
    path: '/notifications',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/NotificationList'),
      './front/NotificationList'
    ),
  },
  {
    name: 'Search',
    screenName: '메인/검색',
    path: '/search',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Search'),
      './front/Search'
    ),
  },
  {
    name: 'SearchTagDetail',
    screenName: '태그',
    path: '/search/tags/:tagId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/TagDetail'),
      './front/TagDetail'
    ),
  },
  {
    name: 'SearchResult',
    screenName: '검색 결과',
    path: '/search-result',
    component: getAsyncComponent(
      () => import('./front/SearchResult'),
      './front/SearchResult'
    ),
  },
  {
    name: 'TagDetail',
    screenName: '태그',
    path: '/tags/:tagId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/TagDetail'),
      './front/TagDetail'
    ),
  },
  {
    name: 'Unregister',
    path: '/unregister',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Unregister'),
      './front/Unregister'
    ),
  },
  {
    name: 'CheerMore',
    screenName: '응원 목록',
    path: '/stories/:storyId(\\d+)/cheers',
    component: getAsyncComponent(
      () => import('./front/CheerMore'),
      './front/CheerMore'
    ),
  },
  {
    name: 'UserList',
    screenName: '유저 목록',
    path: '/user-list/:sectionId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/UserList'),
      './front/UserList'
    ),
  },
  {
    name: 'ImpCertification',
    screenName: '아임포트 인증서',
    path: '/imp-certification',
    component: getAsyncComponent(
      () => import('./front/ImpCertification'),
      './front/ImpCertification'
    ),
  },
  {
    name: 'Coupon',
    screenName: '쿠폰 등록',
    path: '/coupons',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Coupon'),
      './front/Coupon'
    ),
  },
  {
    name: 'Report',
    screenName: '신고하기',
    path: '/report',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/Report'),
      './front/Report'
    ),
  },
  {
    name: 'BlockedUserList',
    screenName: '차단한 유저 목록',
    path: '/blocked-users',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./front/BlockedUserList'),
      './front/BlockedUserList'
    ),
  },
  {
    name: 'Market',
    screenName: '채티마켓',
    path: '/market',
    component: getAsyncComponent(
      () => import('./front/Market'),
      './front/Market'
    ),
  },
  {
    name: 'MarketInventoryList',
    screenName: '채티마켓/보관함',
    path: '/market/inventories',
    component: getAsyncComponent(
      () => import('./front/MarketInventoryList'),
      './front/MarketInventoryList'
    ),
  },
  {
    name: 'Withdrawal',
    screenName: '풍선 환전',
    path: '/withdrawals',
    component: getAsyncComponent(
      () => import('./front/Withdrawal'),
      './front/Withdrawal'
    ),
  },
  {
    name: 'CommunityPostCreate',
    screenName: '커뮤니티/생성',
    path: '/community/posts/create',
    component: getAsyncComponent(
      () => import('./front/CommunityPostCreateEdit'),
      './front/CommunityCreateEdit'
    ),
  },
  {
    name: 'CommunityPostEdit',
    screenName: '커뮤니티/수정',
    path: '/community/posts/:postId(\\d+)/edit',
    component: getAsyncComponent(
      () => import('./front/CommunityPostCreateEdit'),
      './front/CommunityCreateEdit'
    ),
  },
  {
    name: 'CommunityPostDetail',
    screenName: '커뮤니티/상세',
    path: '/community/posts/:postId(\\d+)',
    component: getAsyncComponent(
      () => import('./front/CommunityPostDetail'),
      './front/CommunityDetail'
    ),
  },
  {
    name: 'CommunityPostReplyCommentList',
    screenName: '답글',
    path: '/community/posts/:postId(\\d+)/comments/:commentId(\\d+)/replies',
    component: getAsyncComponent(
      () => import('./front/ReplyCommentList'),
      './front/ReplyCommentList'
    ),
  },
  {
    name: 'FanClub',
    screenName: '팬클럽',
    path: '/users/:userId(\\d+)/fan-club',
    component: getAsyncComponent(
      () => import('./front/FanClub'),
      './front/FanClub'
    ),
  },
  {
    name: 'FanClub',
    screenName: '팬클럽',
    path: '/me/fan-club',
    component: getAsyncComponent(
      () => import('./front/FanClub'),
      './front/FanClub'
    ),
  },
  {
    name: 'FanClubEdit',
    screenName: '팬클럽/수정',
    path: '/me/fan-club/edit',
    component: getAsyncComponent(
      () => import('./front/FanClubEdit'),
      './front/FanClubEdit'
    ),
    auth: SignInRequiredRoute,
  },
  {
    name: 'RestrictionDetail',
    screenName: '정책 위반',
    path: '/restrictions',
    component: getAsyncComponent(
      () => import('./front/RestrictionDetail'),
      './front/RestrictionDetail'
    ),
  },
  // to
  {
    name: 'To',
    path: '/to',
    component: getAsyncComponent(() => import('./front/To'), './front/To'),
  },
  // studio
  {
    name: 'StudioMypage',
    screenName: '스튜디오',
    path: '/studio',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/MyPage'),
      './studio/Mypage'
    ),
  },
  {
    name: 'StudioStoryDetail',
    screenName: '스튜디오/작품 상세',
    path: '/studio/stories/:storyId(\\d+)',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryDetail'),
      './studio/StoryDetail'
    ),
  },
  {
    name: 'StudioStoryCreate',
    screenName: '스튜디오/작품 생성',
    path: '/studio/stories/create',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryCreateEdit'),
      './studio/StoryCreateEdit'
    ),
  },
  {
    name: 'StudioStoryEdit',
    screenName: '스튜디오/작품 수정',
    path: '/studio/stories/:storyId(\\d+)/edit',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryCreateEdit'),
      './studio/StoryCreateEdit'
    ),
  },
  {
    name: 'StudioEpisodeCreate',
    screenName: '스튜디오/회차 작성',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/create',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/EpisodeCreateEdit'),
      './studio/EpisodeCreateEdit'
    ),
  },
  {
    name: 'StudioEpisodeEdit',
    screenName: '스튜디오/회차 작성',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/edit',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/EpisodeCreateEdit'),
      './studio/EpisodeCreateEdit'
    ),
  },
  {
    name: 'StudioEpisodePreview',
    screenName: '스튜디오/회차 미리보기',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/preview',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/EpisodePreview'),
      './studio/EpisodePreview'
    ),
  },
  {
    name: 'StudioStoryAd',
    screenName: '스튜디오/작품 홍보 목록',
    path: '/studio/stories/:storyId(\\d+)/ads',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryAd'),
      './studio/StoryAd'
    ),
  },
  {
    name: 'StudioStoryAdCreate',
    screenName: '스튜디오/작품 홍보 생성',
    path: '/studio/stories/:storyId(\\d+)/ads/create',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryAdCreate'),
      './studio/StoryAdCreate'
    ),
  },
  {
    name: 'StudioStoryAdInfo',
    screenName: '스튜디오/작품 홍보 설명',
    path: '/studio/story-ads/info',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/StoryAd/info'),
      './studio/StoryAd/info'
    ),
  },
  {
    name: 'StudioBookingPublish',
    screenName: '스튜디오/예약 공개',
    path: '/studio/stories/:storyId(\\d+)/episodes/:episodeId(\\d+)/booking',
    auth: SignInRequiredRoute,
    component: getAsyncComponent(
      () => import('./studio/BookingPublish'),
      './studio/BookingPublish'
    ),
  },
  // events
  {
    name: 'Contest',
    screenName: 'contest',
    path: '/events/contest2022',
    component: getAsyncComponent(
      () => import('./events/Contest'),
      'events/Contest'
    ),
  },
  // policy
  {
    name: 'TermsOfService',
    screenName: '서비스 이용약관',
    path: '/terms-of-service',
    component: getAsyncComponent(
      () => import('./policy/TermsOfService'),
      './front/TermsOfService'
    ),
  },
  {
    name: 'TermsOfService',
    screenName: '서비스 이용약관',
    path: '/terms-of-service/:version',
    component: getAsyncComponent(
      () => import('./policy/TermsOfService/version'),
      './front/TermsOfService/version'
    ),
  },
  {
    name: 'PrivacyPolicy',
    screenName: '개인정보 취급방침',
    path: '/privacy-policy',
    component: getAsyncComponent(
      () => import('./policy/PrivacyPolicy'),
      './front/PrivacyPolicy'
    ),
  },
  {
    name: 'PrivacyPolicy',
    screenName: '개인정보 취급방침',
    path: '/privacy-policy/:version',
    component: getAsyncComponent(
      () => import('./policy/PrivacyPolicy/version'),
      './front/PrivacyPolicy/version'
    ),
  },
  // migrations
  {
    name: 'Migration',
    path: '/migrations',
    component: getAsyncComponent(
      () => import('./front/Migration'),
      './front/Migration'
    ),
  },
];

export default routes;



// WEBPACK FOOTER //
// ./src/app/pages/routes.jsx